import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import { SmartToy as AIIcon } from '@mui/icons-material';
import { auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getOrCreateUserProfile } from '../../utils/userService';
import {
  subscribeToAllMessages,
  replyToMessage,
  updateMessageStatus,
  markMessageAsReadByAdmin,
} from '../../utils/messageService';
import { generateAIResponse } from '../../utils/aiAgentService';

function AdminInbox() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0=all, 1=unread, 2=pending, 3=resolved
  const [loading, setLoading] = useState(true);
  
  // Reply form
  const [replyContent, setReplyContent] = useState('');
  const [replying, setReplying] = useState(false);
  
  // AI Assist
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const profile = await getOrCreateUserProfile(user);
        setUserProfile(profile);
        
        if (profile.role !== 'admin') {
          navigate('/');
          return;
        }
        
        // Subscribe to real-time updates
        const unsubMessages = subscribeToAllMessages((messages) => {
          setMessages(messages);
          setLoading(false);
        });
        return () => unsubMessages();
      } else {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    // Filter messages based on active tab
    switch (activeTab) {
      case 1:
        setFilteredMessages(messages.filter((m) => m.status === 'unread'));
        break;
      case 2:
        setFilteredMessages(messages.filter((m) => m.status === 'pending'));
        break;
      case 3:
        setFilteredMessages(messages.filter((m) => m.status === 'resolved'));
        break;
      default:
        setFilteredMessages(messages);
    }
  }, [messages, activeTab]);

  const handleReply = async (markAsResolved = false) => {
    if (!replyContent) {
      alert('Please enter a reply');
      return;
    }

    setReplying(true);
    try {
      const status = markAsResolved ? 'resolved' : 'pending';
      await replyToMessage(selectedMessage.id, replyContent, 'admin', status);
      setReplyContent('');
      alert('✅ Reply sent successfully!');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('❌ Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleMarkResolved = async () => {
    try {
      await updateMessageStatus(selectedMessage.id, 'resolved');
      alert('✅ Message marked as resolved');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('❌ Failed to update status');
    }
  };

  const handleAIAssist = async () => {
    if (!selectedMessage) return;
    
    setAiGenerating(true);
    setAiError(null);
    
    try {
      const result = await generateAIResponse(
        'customer-support',
        selectedMessage.messages,
        {
          userEmail: selectedMessage.userEmail,
          type: selectedMessage.type,
          subject: selectedMessage.subject,
        }
      );
      
      // Populate reply box with AI-generated response
      setReplyContent(result.response);
      console.log('✅ AI response generated:', result.tokensUsed, 'tokens used');
    } catch (error) {
      console.error('Error generating AI response:', error);
      setAiError(error.message || 'Failed to generate AI response');
    } finally {
      setAiGenerating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'unread':
        return 'error';
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'help':
        return '🆘 Help';
      case 'question':
        return '❓ Question';
      case 'refund':
        return '💰 Refund';
      case 'credit':
        return '🎁 Credit';
      default:
        return type;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    let date;
    if (timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      // ISO string
      date = new Date(timestamp);
    } else if (timestamp.seconds) {
      // Firestore Timestamp object
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Just now';
    }
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Typography sx={{ color: 'text.primary' }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '2px solid',
          borderColor: 'divider',
          py: 4,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                👑 Admin Inbox
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Manage user messages and support tickets
              </Typography>
            </Box>
            <Button onClick={() => navigate('/admin')} variant="outlined">
              ← Back to Dashboard
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Stats */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2,
            mb: 3,
          }}
        >
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {messages.length}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Total Messages</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ color: 'error.main', fontWeight: 600 }}>
              {messages.filter((m) => m.status === 'unread').length}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Unread</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ color: 'warning.main', fontWeight: 600 }}>
              {messages.filter((m) => m.status === 'pending').length}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Pending</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 600 }}>
              {messages.filter((m) => m.status === 'resolved').length}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Resolved</Typography>
          </Paper>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '350px 1fr' }, gap: 3 }}>
          {/* Message List */}
          <Paper sx={{ p: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{ mb: 2 }}
            >
              <Tab label="All" />
              <Tab label="Unread" />
              <Tab label="Pending" />
              <Tab label="Resolved" />
            </Tabs>

            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {filteredMessages.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                  No messages
                </Typography>
              ) : (
                <List>
                  {filteredMessages.map((msg) => (
                    <ListItem key={msg.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        selected={selectedMessage?.id === msg.id}
                        onClick={async () => {
                          setSelectedMessage(msg);
                          // Mark as read when admin views the message
                          await markMessageAsReadByAdmin(msg.id);
                        }}
                        sx={{
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: selectedMessage?.id === msg.id ? 'primary.main' : 'divider',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(0,255,245,0.1)',
                            boxShadow: '0 0 10px rgba(0,255,245,0.2)',
                          },
                        }}
                      >
                        <ListItemText
                          primary={msg.subject}
                          secondary={
                            <>
                              <Typography
                                component="span"
                                sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}
                              >
                                {msg.userEmail}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip label={getTypeLabel(msg.type)} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                                <Chip
                                  label={msg.status}
                                  color={getStatusColor(msg.status)}
                                  size="small"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            </>
                          }
                          primaryTypographyProps={{
                            sx: { color: 'text.primary', fontWeight: 600, fontSize: '0.9rem' },
                          }}
                          secondaryTypographyProps={{
                            component: 'div',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>

          {/* Message Detail */}
          <Paper sx={{ p: 3 }}>
            {!selectedMessage ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Select a message to view conversation
                </Typography>
              </Box>
            ) : (
              <>
                {/* Message Header */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                        {selectedMessage.subject}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 1 }}>
                        From: {selectedMessage.userEmail}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={getTypeLabel(selectedMessage.type)} size="small" />
                        <Chip
                          label={selectedMessage.status}
                          color={getStatusColor(selectedMessage.status)}
                          size="small"
                        />
                      </Box>
                    </Box>
                    {selectedMessage.status !== 'resolved' && (
                      <Button variant="outlined" size="small" onClick={handleMarkResolved}>
                        Mark Resolved
                      </Button>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Messages Thread */}
                <Box sx={{ maxHeight: '40vh', overflow: 'auto', mb: 3 }}>
                  {selectedMessage.messages?.map((msg, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: msg.from === 'user' ? 'rgba(0,255,245,0.05)' : 'rgba(157,0,255,0.05)',
                        borderLeft: '3px solid',
                        borderColor: msg.from === 'user' ? 'primary.main' : 'secondary.main',
                        borderRadius: '8px',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
                          {msg.from === 'user' ? selectedMessage.userEmail : 'Support Team (You)'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          {formatTimestamp(msg.timestamp)}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Reply Form */}
                {selectedMessage.status !== 'resolved' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    
                    {/* AI Assist Button */}
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={aiGenerating ? <CircularProgress size={20} /> : <AIIcon />}
                        onClick={handleAIAssist}
                        disabled={aiGenerating}
                        fullWidth
                        sx={{
                          borderColor: 'secondary.main',
                          color: 'secondary.main',
                          '&:hover': {
                            borderColor: 'secondary.dark',
                            bgcolor: 'rgba(157,0,255,0.1)',
                          },
                        }}
                      >
                        {aiGenerating ? 'AI Generating Response...' : '🤖 AI Assist'}
                      </Button>
                      {aiError && (
                        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setAiError(null)}>
                          {aiError}
                        </Alert>
                      )}
                    </Box>
                    
                    <Box>
                      <TextField
                        label="Your Reply"
                        multiline
                        rows={4}
                        fullWidth
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        sx={{ mb: 2 }}
                        helperText={aiGenerating ? "AI is generating a response..." : "Review and edit before sending"}
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => handleReply(false)}
                          disabled={replying || !replyContent}
                          sx={{ flex: 1 }}
                        >
                          {replying ? 'Sending...' : 'Send Reply'}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleReply(true)}
                          disabled={replying || !replyContent}
                          sx={{
                            flex: 1,
                            background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                            '&:hover': {
                              background: 'linear-gradient(90deg, #059669 0%, #047857 100%)',
                            },
                          }}
                        >
                          {replying ? 'Sending...' : 'Send & Resolve'}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}

                {selectedMessage.status === 'resolved' && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography sx={{ color: 'success.main', fontWeight: 600 }}>
                      ✅ This conversation has been resolved
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}

export default AdminInbox;

